import { Params } from '@angular/router';
import { ArrayUtils } from '@app/shared/utils/array-utils';
import { ModelObject } from '@cco/model';
import { DateTime, Duration, DurationLikeObject } from 'luxon';
import { filter, Observable } from 'rxjs';
import { distinctUntilChanged, map, takeUntil, takeWhile } from 'rxjs/operators';
import { enumerablePropertiesAreTheSame } from '@app/shared/utils/object-utils';

export const notNullish = <T> () => (source: Observable<T>) => {
  return source.pipe(
    filter(value => value != null),
  );
};

export const selectStringParam =
  (notifier: Observable<unknown>, paramName: string) => (source: Observable<Params>) => {
    return source.pipe(
      map(params => params[paramName]),
      distinctUntilChanged(),
      takeUntil(notifier),
    );
  };

export const selectNumberParam =
  (notifier: Observable<unknown>, paramName: string) => (source: Observable<Params>) => {
    return source.pipe(
      map((params) => {
        if (params[paramName] == null) {
          return null;
        }
        return +params[paramName];
      }),
      distinctUntilChanged(),
      takeUntil(notifier),
    );
  };

export const selectBooleanParam =
  (notifier: Observable<unknown>, paramName: string) => (source: Observable<Params>) => {
    return source.pipe(
      // tap(params => console.log('params', params)),
      map((params) => {
        const paramStringValue = params[paramName];
        if (paramStringValue == null) {
          return false;
        }

        return (
          paramStringValue === '1' ||
          paramStringValue.toLowerCase().startsWith('t') ||
          paramStringValue.toLowerCase().startsWith('y')
        );
      }),
      distinctUntilChanged(),
      takeUntil(notifier),
    );
  };

/**
 * The reference may either be a DateTime (point in time), or an argument to DateTime.minus(<arg>)
 * or an ISO duration.
 */
export const olderThan =
  (reference?: DateTime | number | Duration | DurationLikeObject | string) =>
  (source: Observable<DateTime | string>) => {
    return source.pipe(
      takeWhile((sourceInstant) => {
        let dateTime: DateTime;

        if (sourceInstant == null || reference == null) {
          return true;
        }

        if (typeof sourceInstant === 'string') {
          dateTime = DateTime.fromISO(sourceInstant);
        } else if (DateTime.isDateTime(sourceInstant)) {
          dateTime = sourceInstant;
        }

        if (DateTime.isDateTime(reference)) {
          return dateTime < reference;
        }

        if (typeof reference === 'string') {
          if (!Duration.fromISO(reference).isValid) {
            throw new Error(`Invalid ISO8601 duration: ${reference}`);
          }

          return dateTime < DateTime.local().minus(Duration.fromISO(reference));
        }

        return dateTime < DateTime.local().minus(reference);
      }),
    );
  };

export const distinctUntilArrayMemberIdsChanged =
  <T extends ModelObject<I>, I>() =>
  (source: Observable<Array<T>>) => {
    return source.pipe(
      distinctUntilChanged((x, y) => !(
          ArrayUtils.aMissingElementFromBByKey(x, y) || ArrayUtils.aMissingElementFromBByKey(y, x)
      )),
    );
  };

export const arrayMemberIdsOrVersionsChangedComparator = <T extends ModelObject<I>, I>(idAccessor: (obj: T) => I, versionAccessor: (obj: T) => string | number) => {
  return (x: T[], y: T[]) => {
    if (x == null && y == null)
      return true;

    return !(
      (x == null && y != null) ||
      (x != null && y == null) ||
      ArrayUtils.aMissingElementFromBByKey(x, y, obj => `${idAccessor(obj)}#${versionAccessor(obj)}`) ||
      ArrayUtils.aMissingElementFromBByKey(y, x, obj => `${idAccessor(obj)}#${versionAccessor(obj)}`)
    );
  };
};

export const distinctUntilArrayMemberIdsOrVersionsChanged =
  <T extends ModelObject<I>, I>() =>
    (source: Observable<Array<T>>) => {
      return source.pipe(
        distinctUntilChanged(arrayMemberIdsOrVersionsChangedComparator(obj => obj.id, obj => obj.version)),
      );
    };

export const distinctUntilArrayMemberAccessedIdsOrAccessedVersionChanged = <T extends ModelObject<I>, I>(idAccessor: (obj: T) => I, versionAccessor: (obj: T) => string | number) => {
  return () =>
    (source: Observable<Array<T>>) => {
      return source.pipe(
        distinctUntilChanged(arrayMemberIdsOrVersionsChangedComparator(idAccessor, versionAccessor)),
      );
    };
  };

export const distinctUntilArrayMembersChanged = <T>() => (source: Observable<T[]>) => source.pipe(
  distinctUntilChanged((a: T[], b: T[]) => {
    return !(ArrayUtils.aMissingElementFromB(a, b) || ArrayUtils.aMissingElementFromB(b, a));
  })
);

/**
 * Perform a shallow comparison of the enumerable properties of an object
 */
export const distinctUntilObjectPropertiesChanged = <T>() => (source: Observable<T>) => source.pipe(
  distinctUntilChanged((a: T, b: T) => enumerablePropertiesAreTheSame(a, b))
);
